'use client';

import { useState, useCallback } from 'react';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { useRouter } from 'next/navigation';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { useAppNotification } from '../../../notifications/NotificationContextProvider';
import { cancelSavingsPlanAction } from '../../../common/gqlActions/cancelSavingsPlan.action';
import {
  datadogErrorHelper,
  useHandleGqlErrorsWithSnackbar,
} from '../../../common';
import { parseHtmlString } from '@bts-web/utils-functions';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';

export interface IUseCancelSavingsPlan {
  savingsPlan: { name: string; id: string };
}

export const useCancelSavingsPlan = ({
  savingsPlan,
}: IUseCancelSavingsPlan) => {
  const { closeAll } = useModalDrawerContext();

  const router = useRouter();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { t } = useClientTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const { setAppNotification } = useAppNotification();

  const onConfirm = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await cancelSavingsPlanAction(savingsPlan.id);

      if (response?.errors) {
        console.info('[ERR][CANCELING SAVINGS PLAN]', response?.errors);

        setIsLoading(false);

        processErrors(response.errors);

        closeAll();
      } else {
        setIsLoading(false);

        closeAll();

        setAppNotification({
          title:
            parseHtmlString(
              t('savings_plan_was_canceled', {
                asset: savingsPlan.name,
              }),
            ) || '',
          visual: 'success',
          customDuration: 4000,
          withClose: true,
        });
      }
    } catch (error) {
      closeAll();

      setIsLoading(false);

      datadogErrorHelper({
        errorMessage: JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });

      router.push('/error/server');
    }
  }, [savingsPlan]);

  return {
    isLoading,
    onConfirm,
  };
};
